/**
 * Lightweight analytics helper.
 *
 * - Sends to Google Analytics (gtag) when configured via the
 *   Google Analytics connector (VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY).
 * - Always dispatches a `window` CustomEvent("analytics") so andere tools /
 *   dashboards kunnen meeluisteren, ook zonder GA-koppeling.
 * - No-op tijdens SSR.
 */

import { hasAnalyticsConsent, CONSENT_EVENT } from "@/lib/consent";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

let initialized = false;
let consentListenerBound = false;

function bindConsentListener() {
  if (consentListenerBound || typeof window === "undefined") return;
  consentListenerBound = true;
  window.addEventListener(CONSENT_EVENT, () => {
    if (hasAnalyticsConsent()) initAnalytics();
  });
}

export function initAnalytics() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  bindConsentListener();
  if (initialized) return;
  if (!hasAnalyticsConsent()) {
    // Zonder consent: geen GA-script, geen dataLayer-pushes.
    return;
  }

  const measurementId = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY;
  if (!measurementId) {
    window.dataLayer = window.dataLayer || [];
    return;
  }

  initialized = true;

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", measurementId, {
    send_page_view: false,
    anonymize_ip: true,
    cookie_flags: "SameSite=None;Secure",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  try {
    gtag("event", "page_view", { page_path: window.location.pathname });
  } catch {
    /* noop */
  }
}

export function trackPageView(path: string) {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", { page_path: path });
    }
  } catch {
    /* noop */
  }
}

export function trackEvent(name: string, params: AnalyticsParams = {}) {
  if (typeof window === "undefined") return;

  if (hasAnalyticsConsent()) {
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", name, params);
      }
    } catch {
      // never break UI on analytics errors
    }
  }

  // Interne CustomEvent blijft ongemoeid: geen persistente opslag/cookies.
  try {
    window.dispatchEvent(new CustomEvent("analytics", { detail: { name, params } }));
  } catch {
    /* noop */
  }
}

/** Track a call-to-action button/link click with its label, destination and section. */
export function trackCtaClick(
  ctaText: string,
  ctaTarget: string,
  ctaSection: string,
  extra: AnalyticsParams = {}
) {
  trackEvent("cta_click", {
    cta_text: ctaText,
    cta_target: ctaTarget,
    cta_section: ctaSection,
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
    ...extra,
  });
}

/**
 * Delegated tracker voor headernavigatie: registreert een `nav_click`-event
 * op basis van de aangeklikte <a>. Detecteert automatisch ankerlinks (#sectie)
 * en logt dan ook een `nav_anchor_scroll`-event.
 */
export function trackNavClickFromEvent(
  event: React.MouseEvent<HTMLElement>,
  navSection: string
) {
  if (typeof window === "undefined") return;
  const anchor = (event.target as HTMLElement | null)?.closest("a");
  if (!anchor) return;

  const rawHref = anchor.getAttribute("href") ?? "";
  const label = (anchor.textContent ?? "").trim().replace(/\s+/g, " ").slice(0, 80);
  const isHashOnly = rawHref.startsWith("#");
  const url = (() => {
    try {
      return new URL(rawHref, window.location.href);
    } catch {
      return null;
    }
  })();
  const hash = isHashOnly ? rawHref : url?.hash ?? "";
  const targetPath = isHashOnly ? window.location.pathname : url?.pathname ?? rawHref;

  trackEvent("nav_click", {
    nav_section: navSection,
    nav_label: label || "(no-label)",
    nav_target: rawHref,
    nav_target_path: targetPath,
    nav_hash: hash || undefined,
    page_path: window.location.pathname,
  });

  if (hash) {
    trackEvent("nav_anchor_scroll", {
      nav_section: navSection,
      anchor_id: hash.replace(/^#/, ""),
      nav_target_path: targetPath,
      page_path: window.location.pathname,
    });
  }
}

